import React from "react";

import { Item, Root, type ToggleGroupSingleProps } from "@radix-ui/react-toggle-group";
import cx from "classnames";
import type { Placement } from "tippy.js";

import { Tooltip } from "~/ui";

import styles from "./ToggleGroup.module.css";

type Option<T> = {
	value: T;
	label?: string | React.ReactNode;
};

export interface ToggleGroupProps<T extends string> extends Omit<ToggleGroupSingleProps, "type" | "asChild"> {
	options: readonly Option<T>[];
	onValueChange: (value: T) => void;
	tooltips?: { [key: string]: string };
	disabledTooltip?: string;
	tooltipPlacement?: Placement;
	className?: string;
	itemClassName?: string;
}

export const ToggleGroup = <T extends string>({
	options,
	onValueChange,
	value,
	tooltips,
	disabledTooltip,
	tooltipPlacement,
	className,
	itemClassName,
	...rest
}: ToggleGroupProps<T>) => {
	const onToggle = React.useCallback(
		(value: T) => {
			if (value) onValueChange(value);
		},
		[onValueChange],
	);

	const content = (
		<Root className={cx(styles.ToggleGroup, className)} value={value} type="single" onValueChange={onToggle} {...rest}>
			{options.map(({ value, label }) => {
				const item = (
					<Item
						key={value}
						value={value}
						className={cx(styles.ToggleGroupItem, itemClassName)}
						data-option-text={value}
						data-tour-tooltip-target={`toggle-group-${value}`}
					>
						{/* The bold text is always rendered but with visibility: hidden.
						    This way we are always making space for the bold text, so things
						    don't shift when the text becomes bold. The visibleText is
						    absolute-ly positioned and centered over the hidden bold text. */}
						<span className={styles.hiddenBoldText}>{label ?? value}</span>
						<span className={styles.visibleText}>{label ?? value}</span>
					</Item>
				);
				return !rest.disabled && tooltips && tooltips[value] ? (
					<Tooltip
						placement={tooltipPlacement ?? "top"}
						maxWidth={220}
						content={<div style={{ textAlign: "center" }}>{tooltips[value]}</div>}
						key={value}
					>
						{item}
					</Tooltip>
				) : (
					item
				);
			})}
		</Root>
	);

	if (rest.disabled && disabledTooltip) {
		return (
			<Tooltip
				placement={tooltipPlacement ?? "left"}
				maxWidth={220}
				content={<div style={{ textAlign: "center" }}>{disabledTooltip}</div>}
			>
				{content}
			</Tooltip>
		);
	}
	return content;
};

export default ToggleGroup;
